<template>
    <TprmModal open additional-class-name="ConfirmSendEntityRequestModal" title="Are you sure?" @close="close">
        <div
            class="ConfirmSendEntityRequestModal__body text-center"
            data-test-id="ConfirmSendEntityRequestModal__body"
        >
            <p data-test-id="ConfirmSendEntityRequestModal__statement">
                <strong>{{ exhaustedSummary }}</strong> selected are currently in the exhausted state.
            </p>

            <!-- The only statement of what Send actually does to each entity. A bulk request names
                 no requirements, so the server reopens everything the risk profile assigns, and
                 this modal offers no way to suppress the email. -->
            <p data-test-id="ConfirmSendEntityRequestModal__bulkNote">
                {{ emailStatement }}
                Requirements cannot be chosen individually in a bulk request.
            </p>
        </div>

        <div class="ConfirmSendEntityRequestModal__actionSection">
            <NewButton
                color="primary"
                class="ConfirmSendEntityRequestModal__submitButton"
                :loading="saving"
                @click="confirm"
            >
                Send Request
            </NewButton>
        </div>

        <div class="ConfirmSendEntityRequestModal__footer">
            <div class="ConfirmSendEntityRequestModal__footerLine">
                <span>To learn more about requesting compliant insurance, visit the </span>
                <a
                    href="https://evidentidsupport.zendesk.com/hc/en-us/articles/4407110845847-On-Demand-Request-Creation"
                    class="ConfirmSendEntityRequestModal__footerLink"
                    target="_blank"
                >
                    Knowledge Base.
                </a>
            </div>
        </div>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { ComplianceStatus, VerificationStatus } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { isEligibleForNewLink } from '@/utils/is-eligible-for-new-link/isEligibleForNewLink';

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'confirm' but it is
    // neither declared in the emits option nor as an 'onClose'/'onConfirm' prop" every
    // time it fires.
    @Component({
        components: {
            NewButton,
            TprmModal,
        },
        emits: [ 'close', 'confirm' ],
    })
    export default class ConfirmSendEntityRequestModal extends Vue {
        @Prop({ type: Array, default: () => [] })
        private entities!: Entity[];

        @Prop({ type: Boolean, default: false })
        private saving!: boolean;

        private get exhaustedEntities(): Entity[] {
            return this.entities.filter(this.isExhausted);
        }

        /** The entities Send actually acts on -- the same narrowing the bulk send applies, so this
         *  modal does not describe what happens to entities it will skip. */
        private get sentEntities(): Entity[] {
            return this.entities.filter(isEligibleForNewLink);
        }

        /** TODO PRODUCT-26539: the paused wording is not from the handoff, which has only the
         *  unqualified sentence. A paused entity still gets a link but is never emailed, so that
         *  sentence is false whenever one is in the selection. */
        private get emailStatement(): string {
            return this.sentEntities.some((entity) => entity.paused)
                ? 'Every entity that is not paused will be emailed and placed on the standard reminder cadence.'
                : 'Every entity will be emailed and placed on the standard reminder cadence.';
        }

        /** Only a multi-entity selection reaches this modal: a single entity goes through the
         *  create-link modal, which collects per-entity choices this confirmation has nowhere to
         *  put. So there is one statement to make, and it is a count. */
        private get exhaustedSummary(): string {
            return `${this.exhaustedEntities.length} of ${this.entities.length} entities`;
        }

        private isExhausted(entity: Entity): boolean {
            return entity.complianceStatus === ComplianceStatus.nonCompliant &&
                entity.verificationStatus === VerificationStatus.exhausted;
        }

        private confirm(): void {
            this.$emit('confirm');
        }

        private close(): void {
            this.$emit('close');
        }
    }
</script>
